import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useState, createRef, useEffect, useRef } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useOnClickOutside } from 'usehooks-ts';
import { useHistory } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import PushPinIcon from '@mui/icons-material/PushPin';
import cx from 'classnames';

import { selectShareFolder, switchShareModal, setClickedFolderName } from '../../../store/folders/foldersSlice';
import { ReactComponent as UsersIcon } from '../../../assets/icons/CollaboratorsUsersIcon.svg';
import { editFolderName, postFolderViewRequest, removeFolderThumbnail, uploadFolderThumbnail } from '../../../api/foldersApi';
import DeleteFolderModal from '../../MyFolders/DeleteFolderModal';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import { SortOptions, SortType } from '../../../enums/SORT';
import { MoreIcon } from '../../../assets/icons/threedots';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { NOTIFICATION } from '../../../enums/ENUMS';
import { FOLDER_TYPE } from '../../../enums/TYPES';
import EditButton from '../../modules/EditButton';
import { PATHS } from '../../../enums/PATHS';
import { bytesToGB, bytesToMB, secondsToHours, useDimensions } from '../../../helpers';
import { Portal } from '../../../helpers/bodyPortal';
import { useOverflowAwareActionMenu } from '../../../helpers/useOverflowAwareActionMenu';
import styles from './ComposerFolder.module.scss';
import TrashButton from '../TrashButton';
import ShareButton from '../ShareButton';
import ShareAccessButton from '../ShareAccessButton';
import MoveButton from '../MoveButton';
import EditMyCollaboratorModal from '../../MyFolders/EditMyCollaboratorModal';
import { Tooltip } from '@mui/material';
import { convertSeconds } from '../../Timeline/utils';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../DateSortHeader';
import ReplaceThumbnailModal from '../ReplaceThumbnailModal';
import ThumbnailButton from '../ThumbnailButton';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';

type TComposerFolder = {
  folder: FOLDER_TYPE;
  data: { page: number; limit: number; search?: string; sort?: SortType; sortOption: SortOptions };
  folderIndex: number;
  showOwnerColumn?: boolean;
  fetchDataCallback?: () => void;
  statusLabel?: string;
  onOpen?: () => void;
  onMove?: (folder: FOLDER_TYPE) => void;
  canMove?: boolean;
  contentLayout?: 'default' | 'videofeeds';
  pinContextType?: string;
  pinContextParentId?: string | number | null;
  onPinUpdated?: () => void;
};

const ComposerFolder: React.FC<TComposerFolder> = ({
  folder,
  data,
  folderIndex,
  showOwnerColumn = true,
  fetchDataCallback,
  statusLabel,
  onOpen,
  onMove,
  canMove = false,
  contentLayout = 'default',
  pinContextType,
  pinContextParentId,
  onPinUpdated,
}) => {
  const canViewEmails = useCanViewEmails();
  const dragTooltipTarget = useRef<{ element: HTMLElement; title: string } | null>(null);
  const hideParentDragTooltip = (event: React.MouseEvent<Element>) => {
    const element = event.currentTarget.closest<HTMLElement>('[draggable="true"][title]');
    if (!element) return;
    dragTooltipTarget.current = { element, title: element.title };
    element.removeAttribute('title');
  };
  const restoreParentDragTooltip = () => {
    if (dragTooltipTarget.current?.element.isConnected) {
      dragTooltipTarget.current.element.title = dragTooltipTarget.current.title;
    }
    dragTooltipTarget.current = null;
  };
  const history = useHistory();
  const notification = useSnackbar();
  const redirectRef = createRef<HTMLDivElement>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [folderName, setFolderName] = useState(folder.name);
  const [thumbnailUrl, setThumbnailUrl] = useState(folder.thumbnail_url || '');
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.user);
  const isPinned = Boolean(folder?.is_pinned);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const { actionMenuRef, triggerRef, menuPosition } = useOverflowAwareActionMenu({
    showMenu: showDotsMenu,
    anchorPosition: menuAnchorPosition,
  });

  useEffect(() => {
    setThumbnailUrl(folder.thumbnail_url || '');
  }, [folder.thumbnail_url]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
  };

  const handleEdit = async () => {
    if (folderName.trim().length === 0) {
      setFolderName(folder.name); // reset the input to the previous value
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      return;
    }

    setIsEditing(!isEditing);
    await editFolderName(folderName, folder.id);
    if (fetchDataCallback) {
      fetchDataCallback();
    }
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  const enableFolderNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const onShareClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    dispatch(selectShareFolder(folder));
    dispatch(switchShareModal(true));
  };

  const onMoveClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    onMove?.(folder);
  };

  const onReplaceThumbnailClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };

  const onPinToggle = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { pinContent, unpinContent } = await import('../../../api/pinnedContentApi');
      if (isPinned) {
        await unpinContent({
          targetType: 'folder',
          targetId: folder.id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      } else {
        await pinContent({
          targetType: 'folder',
          targetId: folder.id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      }
      onPinUpdated?.();
      notification.showMessage(isPinned ? 'Unpinned' : 'Pinned');
      setShowDotsMenu(false);
    } catch {
      notification.showMessage('Failed to update pin');
    }
  };

  const redirectToContent = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isEditing) return;
    if (e.currentTarget.contains(e.target as Node)) {
      dispatch(setClickedFolderName(folder.name));
      postFolderViewRequest(e.currentTarget.id);
      if (onOpen) {
        onOpen();
        return;
      }
      history.push(`${PATHS.VIDEOFEEDS_LIST.path}?folderId=${e.currentTarget.id}&parentPage=${PATHS.MY_FOLDERS.label}`);
    }
  };

  const switchDeleteModal = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteModal(!showDeleteModal);
  };

  const handleFolderIconClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    dispatch(setClickedFolderName(folder.name));
    postFolderViewRequest(folder.id);
    if (onOpen) {
      onOpen();
      return;
    }
    history.push(`${PATHS.VIDEOFEEDS_LIST.path}?folderId=${folder.id}&parentPage=${PATHS.MY_FOLDERS.label}`);
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
      return;
    }

    if (e.type === 'contextmenu') {
      setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    } else {
      setMenuAnchorPosition(null);
    }
    setShowDotsMenu(true);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const [editCollModal, setEditCollModal] = useState(false);
  const switchEditCollModal = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setEditCollModal(!editCollModal);
  };

  const { widthD } = useDimensions();
  const directIds = new Set(folder.collaborators.map((u) => u.id));

  const seen = new Set<number>();
  const uniqueGroupAnalysts = folder?.group_collaborators
    .flatMap((g) => g?.analysts || [])
    .filter((u) => {
      if (directIds.has(u.id) || seen.has(+u.id)) return false;
      seen.add(+u.id);
      return true;
    });

  const allUsers = [...folder.collaborators, ...uniqueGroupAnalysts];
  const canReplaceThumbnail =
    folder.owner?.id === currentUser?.id ||
    allUsers.some((user) => user.id === currentUser?.id) ||
    folder.highest_user_access?.access?.title === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE;

  return (
    <>
      <div
      onClick={redirectToContent}
      onContextMenu={switchDotsMenu}
      id={folder.id}
      className={cx(
        styles.container,
        showDotsMenu && styles.selectedFolder,
        contentLayout === 'videofeeds' && styles.containerVideofeedsLayout,
      )}
      style={{
        position: 'relative',
        boxShadow: isPinned ? 'inset 0 0 0 1px rgba(57, 179, 234, 0.35)' : undefined,
      }}
    >
      <div ref={redirectRef} className={cx(styles.leftSide, contentLayout === 'videofeeds' && styles.leftSideVideofeedsLayout)}>
        <div className={styles.link} id={folder.id} onClick={redirectToContent}>
          <img
            src={thumbnailUrl || FolderIcon}
            alt="folder"
            title="foldericon"
            className={thumbnailUrl ? styles.customThumbnail : undefined}
            style={{ cursor: 'pointer' }}
            onClick={handleFolderIconClick}
          />

          <div />
          {isEditing ? (
            <form
              onClick={(e) => {
                e.stopPropagation();
              }}
              onSubmit={handleEdit}
            >
              <input className={styles.input} onChange={handleChange} value={folderName} onBlur={handleEdit} ref={inputRef} />
            </form>
          ) : (
            <div className={styles.name}>
              <span>{folderName}</span>
            </div>
          )}
        </div>
      </div>
      <div
        className={cx(
          styles.rightSide,
          !showOwnerColumn && styles.rightSideNoOwner,
          contentLayout === 'videofeeds' && styles.rightSideVideofeedsLayout,
          contentLayout === 'videofeeds' && showOwnerColumn && styles.rightSideVideofeedsWithOwner,
          contentLayout === 'videofeeds' && !showOwnerColumn && styles.rightSideVideofeedsNoOwner,
        )}
      >
        {showOwnerColumn && <div className={styles.team}>{folder.owner?.username}</div>}

        {contentLayout === 'videofeeds' && (
          <div className={styles.share}>
            <ShareAccessButton targetId={folder.id} targetType="folder" onClick={onShareClick} />
          </div>
        )}

        <div className={styles.collaborators}>
          {statusLabel ? (
            <div>{statusLabel}</div>
          ) : allUsers.length ? (
            <div className={styles.collaboratorCount} style={{ cursor: 'pointer' }} onClick={switchEditCollModal}>
              <Tooltip
                arrow
                title={
                  <div className={styles.tooltip}>
                    Individual collaborators:
                    {folder.collaborators.map((u) => (
                      <div key={u.email} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {' '}
                        <UsersIcon /> {u?.username || (canViewEmails ? u.email : 'User')}
                      </div>
                    ))}
                    <br />
                    {folder.group_collaborators.map((g) => (
                      <div key={g.title} style={{ marginBottom: '12px' }}>
                        {g.title}
                        <br />
                        {g.analysts.map((u) => (
                          <div
                            key={u.email}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            {' '}
                            <UsersIcon /> {u?.username || (canViewEmails ? u.email : 'User')}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                }
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  onMouseEnter={hideParentDragTooltip}
                  onMouseLeave={restoreParentDragTooltip}
                >
                  <UsersIcon /> {allUsers?.length}
                </div>
              </Tooltip>
            </div>
          ) : (
            <Tooltip title={`Add collaborators`}>
              <AddIcon
                className={styles.addCollIcon}
                onClick={switchEditCollModal}
                onMouseEnter={hideParentDragTooltip}
                onMouseLeave={restoreParentDragTooltip}
              />
            </Tooltip>
          )}
          {contentLayout === 'default' && (
            <ShareAccessButton targetId={folder.id} targetType="folder" onClick={onShareClick} />
          )}
        </div>

        <Tooltip
          title={`${getDateColumnTooltipLabel(data.sortOption)}: ${DateFormatter(getDateColumnTooltipValue(folder, data.sortOption), true)}`}
        >
          <div className={styles.date}>{DateFormatter(getDateColumnValue(folder, data.sortOption), widthD > 1220 ? true : false)}</div>
        </Tooltip>
        <div className={styles.size}>
          <img src={StorageIcon} alt="storage" />
          {bytesToGB(Number(folder?.size)) > 1
            ? `${bytesToGB(Number(folder?.size)).toFixed(2)} GB`
            : `${bytesToMB(Number(folder?.size)).toFixed(0)} MB`}
        </div>

        <div className={styles.duration}>
          {folder?.duration && (
            <Tooltip title={convertSeconds(folder.duration, false, true)}>
              <div>{secondsToHours(folder.duration)}</div>
            </Tooltip>
          )}
        </div>

        <div className={styles.dotsMenuBtn} onClick={switchDotsMenu}>
          <div ref={triggerRef} className={styles.dotIcon}>
            <MoreIcon />
          </div>
        </div>
      </div>
      <DeleteFolderModal
        open={showDeleteModal}
        switchModal={() => setShowDeleteModal(!showDeleteModal)}
        folderId={folder.id}
        data={data}
        onDeleted={fetchDataCallback}
      />
      <Portal>
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            display: showDotsMenu ? 'block' : 'none',
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
            right: 'auto',
          }}
        >
          <div className={styles.menuOption} onClick={enableFolderNameEdit}>
            <EditButton /> Rename
          </div>
          <div className={styles.menuOption} onClick={switchEditCollModal}>
            <UsersIcon className={styles.addCollBtn} /> Add collaborator
          </div>
          <div className={styles.menuOption} onClick={onShareClick}>
            <ShareButton /> Share
          </div>
          {canReplaceThumbnail && (
            <div className={styles.menuOption} onClick={onReplaceThumbnailClick}>
              <ThumbnailButton />
              Replace Folder Icon
            </div>
          )}
          {pinContextType && (
            <div className={styles.menuOption} onClick={onPinToggle}>
              <span
                style={{
                  display: 'inline-flex',
                  width: 32,
                  height: 30,
                  padding: 4,
                  borderRadius: 'var(--ui-border-radius)',
                  background: 'var(--action-menu-icon-bg)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 12,
                  boxSizing: 'border-box',
                }}
              >
                <PushPinIcon sx={{ fontSize: 20, color: '#fff' }} />
              </span>
              {isPinned ? 'Unpin' : 'Pin'}
            </div>
          )}
          {canMove && onMove && (
            <div className={styles.menuOption} onClick={onMoveClick}>
              <MoveButton /> Move to
            </div>
          )}
          <div className={styles.menuOption} onClick={switchDeleteModal}>
            <TrashButton /> Delete
          </div>
        </div>
      </Portal>

      {currentUser?.id && (
        <EditMyCollaboratorModal
          open={editCollModal}
          setOpen={setEditCollModal}
          currentUserId={currentUser.id}
          alreadyCollaborators={folder?.collaborators}
          folderId={folder.id}
          ownerEmail={folder.owner.email}
          group_collaborators={folder.group_collaborators}
          folder_name={folder.name}
        />
      )}
      </div>
      <ReplaceThumbnailModal
        open={showThumbnailModal}
        setOpen={setShowThumbnailModal}
        title="Replace Folder Icon"
        cropTitle="Crop Folder Icon"
        cropMode="square"
        onUpload={(data) => uploadFolderThumbnail(folder.id, data)}
        onUploaded={(updatedFolder) => setThumbnailUrl(updatedFolder.thumbnail_url || '')}
        onRestore={thumbnailUrl ? () => removeFolderThumbnail(folder.id) : undefined}
        restoreLabel="Restore Default Folder Icon"
        preview={{ type: 'folder', title: folderName, imageUrl: thumbnailUrl }}
      />
    </>
  );
};

export default ComposerFolder;
